import { useState, useEffect } from 'react';
import { Search, Filter, Calendar } from 'lucide-react';
import api from '../../utils/api';
import './LogsViewer.scss';

const LogsViewer = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        page: 1,
        limit: 50,
        action: '',
        userType: '',
        userEmail: '',
        targetType: '',
        search: '',
        startDate: '',
        endDate: ''
    });
    const [pagination, setPagination] = useState({});
    const [selectedLog, setSelectedLog] = useState(null);

    useEffect(() => {
        fetchLogs();
    }, [filters]);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            Object.keys(filters).forEach(key => {
                if (filters[key]) {
                    params.append(key, filters[key]);
                }
            });

            const response = await api.get(`/admin/logs?${params.toString()}`);
            if (response.data.success) {
                setLogs(response.data.logs);
                setPagination(response.data.pagination);
            }
        } catch (error) {
            console.error('Error fetching logs:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleString();
    };

    const actionColors = {
        login: '#22C55E',
        logout: '#EF4444',
        mosque_create: '#3B82F6',
        mosque_update: '#F59E0B',
        mosque_delete: '#EF4444',
        mosque_view: '#6B7280',
        mosques_list: '#6B7280',
        mosques_filter: '#6B7280',
        comment_add: '#10B981',
        image_upload: '#8B5CF6',
        visit_record: '#3B82F6',
        visit_view: '#6366F1',
        campaign_send: '#8B5CF6',
        analytics_view: '#10B981',
        logs_view: '#6366F1',
        contact_submit: '#F59E0B',
        profile_view: '#6366F1'
    };

    const getUserTypeBadge = (userType) => {
        const colors = {
            admin: '#EF4444',
            user: '#3B82F6',
            anonymous: '#6B7280'
        };
        return colors[userType] || '#6B7280';
    };

    return (
        <div className="logs-viewer">
            <h2>Activity Logs</h2>

            <div className="filters-panel">
                <div className="search-box">
                    <Search size={18} />
                    <input
                        type="text"
                        placeholder="Search logs..."
                        value={filters.search}
                        onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                    />
                </div>

                <select
                    value={filters.userType}
                    onChange={(e) => setFilters({ ...filters, userType: e.target.value, page: 1 })}
                >
                    <option value="">All User Types</option>
                    <option value="admin">Admin</option>
                    <option value="user">User</option>
                    <option value="anonymous">Anonymous</option>
                </select>

                <select
                    value={filters.action}
                    onChange={(e) => setFilters({ ...filters, action: e.target.value, page: 1 })}
                >
                    <option value="">All Actions</option>
                    <option value="login">Login</option>
                    <option value="logout">Logout</option>
                    <option value="mosque_create">Create Mosque</option>
                    <option value="mosque_update">Update Mosque</option>
                    <option value="mosque_delete">Delete Mosque</option>
                    <option value="mosque_view">View Mosque</option>
                    <option value="mosques_list">List Mosques</option>
                    <option value="comment_add">Add Comment</option>
                    <option value="image_upload">Upload Image</option>
                    <option value="visit_record">Record Visit</option>
                    <option value="visit_view">View Visits</option>
                    <option value="campaign_send">Send Campaign</option>
                    <option value="analytics_view">View Analytics</option>
                    <option value="logs_view">View Logs</option>
                    <option value="contact_submit">Contact Submit</option>
                    <option value="profile_view">View Profile</option>
                </select>

                <select
                    value={filters.targetType}
                    onChange={(e) => setFilters({ ...filters, targetType: e.target.value, page: 1 })}
                >
                    <option value="">All Types</option>
                    <option value="mosque">Mosque</option>
                    <option value="campaign">Campaign</option>
                    <option value="contributor">Contributor</option>
                    <option value="comment">Comment</option>
                    <option value="image">Image</option>
                    <option value="system">System</option>
                </select>

                <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters({ ...filters, startDate: e.target.value, page: 1 })}
                    placeholder="Start Date"
                />

                <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters({ ...filters, endDate: e.target.value, page: 1 })}
                    placeholder="End Date"
                />
            </div>

            {loading ? (
                <div className="loading">Loading logs...</div>
            ) : (
                <>
                    <div className="logs-table-container">
                        <table className="logs-table">
                            <thead>
                                <tr>
                                    <th>Timestamp</th>
                                    <th>User Type</th>
                                    <th>User</th>
                                    <th>Action</th>
                                    <th>Target</th>
                                    <th>Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map((log) => (
                                    <tr
                                        key={log._id}
                                        onClick={() => setSelectedLog(log)}
                                        className="log-row"
                                    >
                                        <td>{formatDate(log.timestamp)}</td>
                                        <td>
                                            <span
                                                className="user-type-badge"
                                                style={{ backgroundColor: getUserTypeBadge(log.userType) }}
                                            >
                                                {log.userType}
                                            </span>
                                        </td>
                                        <td>
                                            {log.userEmail ? (
                                                <div className="user-info">
                                                    <strong>{log.userName || log.userEmail}</strong>
                                                    <small>{log.userEmail}</small>
                                                </div>
                                            ) : (
                                                <span className="anonymous-user">Anonymous</span>
                                            )}
                                        </td>
                                        <td>
                                            <span
                                                className="action-badge"
                                                style={{ backgroundColor: actionColors[log.action] || '#6B7280' }}
                                            >
                                                {log.action.replace(/_/g, ' ')}
                                            </span>
                                        </td>
                                        <td>
                                            {log.targetType && log.targetId ? (
                                                <span>{log.targetType}: {log.targetId}</span>
                                            ) : (
                                                <span>-</span>
                                            )}
                                        </td>
                                        <td>
                                            <button className="view-details-btn">
                                                View Details
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {pagination.pages > 1 && (
                        <div className="pagination">
                            <button
                                disabled={filters.page === 1}
                                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                            >
                                Previous
                            </button>
                            <span>
                                Page {pagination.page} of {pagination.pages} ({pagination.total} total)
                            </span>
                            <button
                                disabled={filters.page === pagination.pages}
                                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}

            {selectedLog && (
                <div className="log-modal" onClick={() => setSelectedLog(null)}>
                    <div className="log-modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Log Details</h3>
                        <div className="log-details">
                            <div className="detail-item">
                                <strong>Action:</strong> {selectedLog.action.replace(/_/g, ' ')}
                            </div>
                            <div className="detail-item">
                                <strong>User Type:</strong> 
                                <span 
                                    className="user-type-badge"
                                    style={{ 
                                        backgroundColor: getUserTypeBadge(selectedLog.userType),
                                        marginLeft: '8px',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        color: 'white',
                                        fontSize: '0.85rem'
                                    }}
                                >
                                    {selectedLog.userType}
                                </span>
                            </div>
                            {selectedLog.userName && (
                                <div className="detail-item">
                                    <strong>User Name:</strong> {selectedLog.userName}
                                </div>
                            )}
                            {selectedLog.userEmail && (
                                <div className="detail-item">
                                    <strong>User Email:</strong> {selectedLog.userEmail}
                                </div>
                            )}
                            {selectedLog.userId && (
                                <div className="detail-item">
                                    <strong>User ID:</strong> {selectedLog.userId}
                                </div>
                            )}
                            <div className="detail-item">
                                <strong>Timestamp:</strong> {formatDate(selectedLog.timestamp)}
                            </div>
                            {selectedLog.targetType && (
                                <div className="detail-item">
                                    <strong>Target Type:</strong> {selectedLog.targetType}
                                </div>
                            )}
                            {selectedLog.targetId && (
                                <div className="detail-item">
                                    <strong>Target ID:</strong> {selectedLog.targetId}
                                </div>
                            )}
                            <div className="detail-item">
                                <strong>IP Address:</strong> {selectedLog.ipAddress || 'N/A'}
                            </div>
                            <div className="detail-item">
                                <strong>User Agent:</strong> {selectedLog.userAgent || 'N/A'}
                            </div>
                            <div className="detail-item full-width">
                                <strong>Details:</strong>
                                <pre>{JSON.stringify(selectedLog.details, null, 2)}</pre>
                            </div>
                        </div>
                        <button className="close-button" onClick={() => setSelectedLog(null)}>
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LogsViewer;
